import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';

import QuestionsListItems from '@/components/atom/QuestionsListItems/QuestionsListItems';
import SearchField from '@/components/atom/SearchField/SearchField';
import useRovingTabIndex from '@/hooks/useRovingTabIndex';
import { EFaqsCategoryIcons } from '@/store/faq/config';
import { EOrientation, SMALL_SCREEN_QUERY } from '@/utils/constants';

import CategoryControl from './CategoryControl/CategoryControl';
import {
  CATEGORY_ORDER,
  EXAMPLE_QUESTION_PANEL_ID,
  EXAMPLE_QUESTION_PANEL_MESSAGES,
  EXAMPLE_QUESTION_PANEL_TEST_IDS,
} from './constants';
import {
  CategoryRow,
  PanelCloseButton,
  PanelSearchWrapper,
  PanelToolbarControls,
  PanelToolbarRow,
  PanelWrapper,
  QuestionsListWrapper,
} from './ExampleQuestionPanel.styles';
import { TExampleQuestionPanelProps } from './types';

const ExampleQuestionPanel = ({
  isOpen,
  isOnboarding,
  isBelowComposer,
  activeCategory,
  searchValue,
  faqsList,
  shouldClaimFocus,
  onClose,
  onFilterChange,
  onSearchChange,
  onSelectQuestion,
  onFocusClaimed,
}: TExampleQuestionPanelProps) => {
  const intl = useIntl();
  const panelRef = useRef<HTMLDivElement>(null);

  // INFO: The Filter Toolbar stacks vertically below the breakpoint, so the arrow-key axis follows
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN_QUERY });
  const orientation: EOrientation = isSmallScreen ? EOrientation.VERTICAL : EOrientation.HORIZONTAL;

  /** Clears the Search Term. The field only surrenders Escape while it still holds one, so this
   * never doubles as a request to close the panel. */
  const handleSearchEscape = useCallback(() => onSearchChange(''), [onSearchChange]);

  /** True while a Category Filter must not respond, which is the onboarding lock to Perform. */
  const isCategoryInoperable = useCallback(
    (category: EFaqsCategoryIcons) => isOnboarding && category !== EFaqsCategoryIcons.PERFORM,
    [isOnboarding],
  );

  /** Applies or clears the Category Filter the roving tab index activated by index. */
  const handleActivate = useCallback(
    (index: number) => {
      const category = CATEGORY_ORDER[index];

      if (isCategoryInoperable(category)) return;

      // INFO: The hook decides whether this applies or clears the filter — see setFilterFromPanel.
      onFilterChange(category);
    },
    [isCategoryInoperable, onFilterChange],
  );

  /** Tells the roving tab index which Category Filters to skip while navigating. */
  const isItemDisabledForRoving = useCallback(
    (index: number) => isCategoryInoperable(CATEGORY_ORDER[index]),
    [isCategoryInoperable],
  );

  const { handleKeyDown, getTabIndex, setItemRef } = useRovingTabIndex({
    itemCount: CATEGORY_ORDER.length,
    orientation,
    isItemDisabled: isItemDisabledForRoving,
    onActivate: handleActivate,
  });

  useEffect(() => {
    // INFO: shouldClaimFocus is raised only by user-initiated opens (Category Shortcut, Panel
    // Toggle), never by the onboarding auto-open, which would otherwise pull focus away from the
    // composer's arrival focus. onFocusClaimed clears the request once it has been honoured.
    if (!shouldClaimFocus) return;

    panelRef.current?.focus();
    onFocusClaimed();
  }, [shouldClaimFocus, onFocusClaimed]);

  const panelLabel = intl.formatMessage({ id: EXAMPLE_QUESTION_PANEL_MESSAGES.panelLabel });
  const closeLabel = intl.formatMessage({ id: EXAMPLE_QUESTION_PANEL_MESSAGES.panelClose });
  const toolbarLabel = intl.formatMessage({ id: EXAMPLE_QUESTION_PANEL_MESSAGES.filterToolbar });

  return (
    <PanelWrapper
      ref={panelRef}
      id={EXAMPLE_QUESTION_PANEL_ID}
      role="region"
      aria-label={panelLabel}
      tabIndex={-1}
      $open={isOpen}
      $isBelowComposer={isBelowComposer}
      inert={isOpen ? undefined : ''}
      data-testid={EXAMPLE_QUESTION_PANEL_TEST_IDS.panel}
    >
      <PanelToolbarRow>
        <PanelToolbarControls>
          <CategoryRow
            role="toolbar"
            aria-label={toolbarLabel}
            aria-orientation={orientation}
            onKeyDown={handleKeyDown}
          >
            {CATEGORY_ORDER.map((category, index) => (
              <CategoryControl
                key={category}
                category={category}
                isActive={activeCategory === category}
                isInoperable={isCategoryInoperable(category)}
                tabIndex={getTabIndex(index)}
                testId={`${EXAMPLE_QUESTION_PANEL_TEST_IDS.categoryFilterPrefix}${category}`}
                itemRef={setItemRef(index)}
                onSelect={onFilterChange}
              />
            ))}
          </CategoryRow>

          <PanelSearchWrapper>
            <SearchField
              value={searchValue}
              isDisabled={isOnboarding}
              testId={EXAMPLE_QUESTION_PANEL_TEST_IDS.panelSearch}
              onSearch={onSearchChange}
              onEscape={handleSearchEscape}
            />
          </PanelSearchWrapper>
        </PanelToolbarControls>

        <PanelCloseButton
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          data-testid={EXAMPLE_QUESTION_PANEL_TEST_IDS.panelClose}
        >
          <span className="rioglyph rioglyph-remove" aria-hidden="true" />
        </PanelCloseButton>
      </PanelToolbarRow>

      <QuestionsListWrapper>
        <QuestionsListItems
          isOnboarding={isOnboarding}
          faqsList={faqsList}
          searchValue={searchValue}
          handleSelection={onSelectQuestion}
        />
      </QuestionsListWrapper>
    </PanelWrapper>
  );
};

export default ExampleQuestionPanel;
